import type { AddonInstallation, Meta, MetaLink, MetadataLinkUnavailable, Video } from './index.ts';
import { contentCertification } from './home-hero.ts';
import { isLiveChannel, programmeTime } from './live.ts';

const object = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const text = (v: unknown, max: number) => typeof v === 'string' && v.trim() && v.length <= max && !/[\u0000-\u0008\u000b-\u001f\u007f]/.test(v) ? v : undefined;
const identity = (v: unknown, max = 180) => typeof v === 'string' && v.length > 0 && v.length <= max && v !== '.' && v !== '..' && !/[\u0000-\u001f\u007f]/.test(v) ? v : undefined;
const names = (v: unknown) => Array.isArray(v) ? [...new Set(v.slice(0, 100).flatMap(n => text(n, 300) ? [n as string] : []))] : undefined;

/** Display/navigation URLs only, never a fetch/proxy authorization. Source links
 * and configured addon transports have their own server-side handling. */
export function metadataHttps(value: unknown): string | undefined {
  if (typeof value !== 'string' || value.length > 3000 || /[\u0000-\u0020\u007f\\]/.test(value)) return;
  try {
    const url = new URL(value), host = url.hostname.toLowerCase();
    if (url.protocol !== 'https:' || url.username || url.password || (url.port && url.port !== '443') ||
        !host.includes('.') || host.endsWith('.') || /^\d+(\.\d+){3}$/.test(host) || host.includes(':') ||
        /(^|\.)(localhost|local|internal|lan|home|arpa)$/.test(host)) return;
    return url.href;
  } catch { return; }
}
export type MetadataDiscover = { kind: 'discover'; addonId: string; type: string; catalogId: string; extra: Record<string, string> };
export type MetadataAction = { kind: 'external'; url: string } | { kind: 'search'; query: string } | MetadataDiscover |
  { kind: 'detail'; type: string; id: string; videoId?: string };
export const metadataLinkUnavailable: Record<MetadataLinkUnavailable, string> = {
  'not-installed': 'This catalog addon is not installed in this household.',
  disabled: 'Enable this catalog addon in Addons.',
  'configuration-required': 'Configure this catalog addon in Addons.',
  'unsupported-catalog': 'This addon no longer offers the linked catalog or filters.',
  'invalid-link': 'The addon supplied an unsupported catalog link.',
  'link-limit': 'This response contains too many different catalog links.',
};
export function metadataDiscoverUrl(action: MetadataDiscover): string {
  const query = new URLSearchParams(action.extra).toString();
  return `streamit:///discover/${encodeURIComponent(action.addonId)}/${encodeURIComponent(action.type)}/${encodeURIComponent(action.catalogId)}${query ? '?' + query : ''}`;
}
/** Only opaque installation identities belong in client-side Discover links. */
export function metadataDiscover(value: unknown): MetadataDiscover | undefined {
  if (typeof value !== 'string' || value.length > 40000 || !value.startsWith('streamit:///discover/') || /[\u0000-\u001f\u007f\\]/.test(value)) return;
  try {
    const url = new URL(value), path = url.pathname.split('/');
    if (url.host || url.hash || path.length !== 5) return;
    const addonId = decodeURIComponent(path[2]), type = identity(decodeURIComponent(path[3]), 40), catalogId = identity(decodeURIComponent(path[4]));
    if (!(addonId === 'builtin-cinemeta' || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(addonId)) || !type || !catalogId) return;
    const entries = [...url.searchParams];
    if (entries.length > 12 || new Set(entries.map(([key]) => key)).size !== entries.length ||
        entries.some(([key, v]) => !identity(key) || v.length > 2000 || /[\u0000-\u001f\u007f]/.test(v))) return;
    return { kind: 'discover', addonId, type, catalogId, extra: Object.fromEntries(entries) };
  } catch { return; }
}
export function metadataAction(value: unknown): MetadataAction | undefined {
  const discover = metadataDiscover(value); if (discover) return discover;
  const external = metadataHttps(value);
  if (external) return { kind: 'external', url: external };
  if (typeof value !== 'string' || value.length > 3000 || !value.startsWith('stremio:///') || /[\u0000-\u001f\u007f\\]/.test(value)) return;
  try {
    const url = new URL(value);
    if (url.host || url.hash || url.username || url.password) return;
    if (url.pathname === '/search' && [...url.searchParams.keys()].every(k => k === 'search') && url.searchParams.getAll('search').length === 1) {
      const query = identity(url.searchParams.get('search'), 200)?.trim();
      if (query) return { kind: 'search', query };
    }
    const path = url.pathname.split('/');
    if (path[1] === 'detail' && (path.length === 4 || path.length === 5) && !url.search) {
      const type = identity(decodeURIComponent(path[2]), 40), id = identity(decodeURIComponent(path[3]));
      const videoId = path.length === 5 ? identity(decodeURIComponent(path[4])) : undefined;
      if (type && id && (path.length === 4 || videoId)) return { kind: 'detail', type, id, ...(videoId ? { videoId } : {}) };
    }
    // Discover links can contain a configured transport/key. They must be mapped
    // to an authorized installation on the server before entering public models.
  } catch { return; }
}
function actionUrl(action: MetadataAction) {
  if (action.kind === 'discover') return metadataDiscoverUrl(action);
  if (action.kind === 'external') return action.url;
  if (action.kind === 'search') return `stremio:///search?search=${encodeURIComponent(action.query)}`;
  return `stremio:///detail/${encodeURIComponent(action.type)}/${encodeURIComponent(action.id)}${action.videoId ? '/' + encodeURIComponent(action.videoId) : ''}`;
}
export function metadataLinks(value: unknown, allowMapped = true): MetaLink[] | undefined {
  if (!Array.isArray(value)) return;
  const seen = new Set<string>();
  return value.slice(0, 100).flatMap(v => {
    if (!object(v)) return [];
    const name = text(v.name, 300), category = text(v.category, 80), action = metadataAction(v.url);
    const unavailable = allowMapped && v.url === '' && typeof v.unavailable === 'string' && Object.hasOwn(metadataLinkUnavailable, v.unavailable)
      ? v.unavailable as MetadataLinkUnavailable : undefined;
    if (!name || !category || (!action && !unavailable) || (action?.kind === 'discover' && !allowMapped)) return [];
    const url = action ? actionUrl(action) : '', key = JSON.stringify([name, category, url, unavailable]);
    if (seen.has(key)) return []; seen.add(key); return [{ name, category, url, ...(unavailable ? { unavailable } : {}) }];
  });
}
export function metadataTrailers(...values: unknown[]): Meta['trailerStreams'] {
  const result: NonNullable<Meta['trailerStreams']> = [], seen = new Set<string>();
  for (const value of values) for (const v of (Array.isArray(value) ? value.slice(0, 30) : [])) {
    if (!object(v)) continue;
    const id = v.ytId ?? ((v.type === 'Trailer' || v.type === 'Clip') ? v.source : undefined);
    if (typeof id !== 'string' || !/^[A-Za-z0-9_-]{11}$/.test(id) || seen.has(id) || result.length >= 30) continue;
    seen.add(id); result.push({ ytId: id, title: text(v.title, 300) || (v.type === 'Clip' ? 'Clip' : 'Trailer') });
  }
  return result.length ? result : undefined;
}
type Details = Pick<Meta, 'contentRating' | 'posterShape' | 'logo' | 'director' | 'cast' | 'released' | 'language' | 'country' | 'awards' | 'website' | 'links' | 'trailerStreams' | 'behaviorHints' | 'trailerSourceAddonId'>;
function metadataHints(value: unknown): Meta['behaviorHints'] {
  if (!object(value)) return;
  const id = identity(value.defaultVideoId);
  const hints = { ...(id ? { defaultVideoId: id } : {}), ...(value.isLive === true ? { isLive: true } : {}),
    ...(value.hasScheduledVideos === true ? { hasScheduledVideos: true } : {}) };
  return Object.keys(hints).length ? hints : undefined;
}
export function metadataProgramme(v: Record<string, unknown>, allowMapped = true): Pick<Video, 'startTime' | 'endTime' | 'runtime' | 'releaseInfo' | 'genres' | 'cast' | 'directors' | 'links' | 'ratings'> {
  const start = programmeTime(v.startTime), end = programmeTime(v.endTime);
  return {
    ...(start !== undefined && end !== undefined && end > start ? { startTime: new Date(start).toISOString(), endTime: new Date(end).toISOString() } : {}),
    runtime: text(v.runtime, 100), releaseInfo: text(v.releaseInfo, 100), genres: names(v.genres)?.slice(0, 60),
    cast: names(v.cast), directors: names(v.directors), links: metadataLinks(v.links, allowMapped),
    ratings: Array.isArray(v.ratings) ? v.ratings.slice(0, 20).flatMap(r => object(r) && text(r.value, 100)
      ? [{ value: r.value as string, system: text(r.system, 100), icon: metadataHttps(r.icon) }] : []) : undefined,
  };
}
/** Invalid optional enrichment never removes an otherwise valid title. Explicit
 * projection discards arbitrary properties, stream headers and provider keys. */
export function metadataDetails(v: Record<string, unknown>, allowMapped = true): Details {
  return {
    contentRating: contentCertification(v.contentRating),
    posterShape: v.posterShape === 'square' || v.posterShape === 'landscape' || v.posterShape === 'poster' ? v.posterShape : undefined,
    logo: metadataHttps(v.logo), director: names(v.director), cast: names(v.cast),
    released: text(v.released, 80), language: text(v.language, 300), country: text(v.country, 300), awards: text(v.awards, 4000),
    website: metadataHttps(v.website), links: metadataLinks(v.links, allowMapped), trailerStreams: metadataTrailers(v.trailerStreams, v.trailers),
    behaviorHints: metadataHints(v.behaviorHints),
    trailerSourceAddonId: allowMapped && typeof v.trailerSourceAddonId === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(v.trailerSourceAddonId) ? v.trailerSourceAddonId : undefined,
  };
}
/** Use only addon-declared identities. A metadata hint selects a source list;
 * it never authorizes playback, a download or a provider operation. */
export function defaultVideo(meta: Meta): string | undefined {
  return isLiveChannel(meta) ? undefined : identity(meta.behaviorHints?.defaultVideoId);
}
export function firstVideo(meta: Meta): string | undefined {
  if (isLiveChannel(meta)) return meta.id;
  return defaultVideo(meta) || (meta.type === 'movie' ? meta.id :
    meta.videos?.find(v => v.season === 1 && v.episode === 1)?.id || meta.videos?.[0]?.id ||
    (meta.type === 'series' ? undefined : meta.id));
}
/** Presence, including an empty embedded list, excludes other source addons.
 * A missing origin remains an error; it must not turn into a fallback query.
 * The backend independently refetches this exact metadata/video under auth. */
export function embeddedVideoSource(meta: Meta, videoId: string): { addonId?: string } | undefined {
  if (!meta.videos?.some(v => v.id === videoId && v.embeddedStreams === true)) return;
  return { addonId: typeof meta.sourceAddonId === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(meta.sourceAddonId) ? meta.sourceAddonId : undefined };
}
/** An embedded marker cannot silently turn into ordinary addon discovery. */
export function embeddedSourceAddon(meta: Meta, videoId: string, addons: AddonInstallation[]): AddonInstallation | undefined {
  const source = embeddedVideoSource(meta, videoId);
  if (!source) return;
  if (!source.addonId) throw Error("Refresh this title to identify the addon supplying this video's sources.");
  const addon = addons.find(a => a.id === source.addonId);
  if (!addon) throw Error("This video's addon is no longer installed. Refresh the title or check Addons.");
  if (!addon.enabled) throw Error("Enable this video's addon in Addons, then refresh its sources.");
  if (addon.manifest.behaviorHints?.configurationRequired) throw Error("Configure this video's addon in Addons, then refresh the title.");
  return addon;
}
export type TrailerSource = 'trailer' | 'video-trailer';
/** Trailers remain exclusive to the metadata installation that declared them. */
export function trailerSourceAddon(meta: Meta, addons: AddonInstallation[], videoId?: string): AddonInstallation {
  if (videoId !== undefined && !meta.videos?.some(v => v.id === videoId && v.embeddedTrailers))
    throw Error('These video trailers are no longer available. Refresh the title.');
  const id = videoId === undefined ? meta.trailerSourceAddonId : meta.sourceAddonId;
  if (!id || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(id))
    throw Error('Refresh this title to identify the addon supplying its trailers.');
  const addon = addons.find(a => a.id === id);
  if (!addon) throw Error("This title's trailer addon is no longer installed. Refresh the title or check Addons.");
  if (!addon.enabled) throw Error("Enable this title's trailer addon in Addons, then refresh its sources.");
  if (addon.manifest.behaviorHints?.configurationRequired) throw Error("Configure this title's trailer addon in Addons, then refresh its sources.");
  return addon;
}
/** A borrowed video list keeps its own provenance even when title details came
 * from a different addon. The server rechecks that origin at source selection. */
export function fallbackVideos(meta: Meta, fallback: Meta): Meta {
  if (isLiveChannel(meta) || meta.videos?.length || !fallback.videos?.length) return meta;
  return { ...meta, videos: fallback.videos, sourceAddonId: fallback.sourceAddonId };
}
/** A progress/source snapshot is not the entire detail page. Keep identities
 * exact and omit enrichment, videos and source hints from repeated requests. */
export function playbackMetadata(meta: Meta): Meta {
  return { id: meta.id, type: meta.type, name: meta.name, poster: metadataHttps(meta.poster), background: metadataHttps(meta.background),
    posterShape: meta.posterShape, description: meta.description?.slice(0, 4000), genres: meta.genres?.slice(0, 12),
    releaseInfo: meta.releaseInfo, runtime: meta.runtime, imdbRating: meta.imdbRating,
    ...(isLiveChannel(meta) ? { behaviorHints: { isLive: true as const } } : {}) };
}
